'use client';

import React, { useEffect, useState, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { LuPencilRuler } from 'react-icons/lu';
import { cn } from '@core/helpers/misc/GeneralUtils';
import { useTheme } from '@core/helpers/react/hooks/useTheme';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@core/ui/primitives/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@core/ui/primitives/dialog';
import { Button } from '@/core/ui/primitives/button';

// Dynamically import the wrapper which contains the scoped CSS import
// This ensures Excalidraw's CSS is only loaded when the component is rendered
const ExcalidrawWrapper = dynamic(() => import('./ExcalidrawWrapper').then((mod) => mod.ExcalidrawWrapper), {
  ssr: false,
  loading: () => (
    <div className='excalidraw-loading flex h-full w-full items-center justify-center text-muted-foreground'>
      Loading Excalidraw...
    </div>
  ),
});

interface ExcalidrawDialogTriggerProps {
  className?: string;
}

export function ExcalidrawDialogTrigger({ className = '' }: ExcalidrawDialogTriggerProps) {
  const [open, setOpen] = useState(false);
  const [isMac, setIsMac] = useState(false);
  const { theme, isLoaded } = useTheme();
  const shortcutKey = isMac ? '⌘' : 'Ctrl';

  // Default canvas background.
  //
  // Note: In Excalidraw dark theme, the <canvas> is rendered with an invert filter
  // (`.excalidraw.theme--dark canvas { filter: var(--theme-filter); }` in
  // `@excalidraw/excalidraw/index.css`). Because of this, to *render* a dark-looking
  // canvas background we must set a light `viewBackgroundColor` value.
  const viewBackgroundColor = useMemo(() => {
    // Dark mode: white inverts to Excalidraw's dark surface.
    // Light mode: white matches toolbar islands for unified look.
    return '#ffffff';
  }, [theme]);

  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    const platform = navigator.platform ?? '';
    setIsMac(/mac|ipad|iphone|ipod/i.test(platform));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return () => {};

    const onOpen = () => setOpen(true);
    const onToggle = () => setOpen((prev) => !prev);

    window.addEventListener('excalidraw:open', onOpen);
    window.addEventListener('hotkey:excalidraw.toggle', onToggle);

    return () => {
      window.removeEventListener('excalidraw:open', onOpen);
      window.removeEventListener('hotkey:excalidraw.toggle', onToggle);
    };
  }, []);

  return (
    <>
      <TooltipProvider>
        <Tooltip delayDuration={60}>
          <TooltipTrigger asChild>
            <Button
              type='button'
              variant='ghost'
              size='sm'
              onClick={() => setOpen(true)}
              className={cn(
                'excalidraw-trigger flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-transparent transition-colors hover:bg-primary/10',
                className
              )}
              aria-label={`Excalidraw (${shortcutKey} + E)`}>
              <LuPencilRuler className='w-4 h-4' />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Excalidraw ({shortcutKey} E)</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          showCloseButton={false}
          className={cn(
            'excalidraw-dialog p-0 sm:rounded-xl shadow-xl outline-none',
            'max-w-none sm:max-w-none',
            'w-[calc(100vw-2rem)] h-[calc(100vh-2rem)]',
            'flex flex-col'
          )}
          onEscapeKeyDown={(event: KeyboardEvent) => {
            const t = event.target as HTMLElement | null;
            const tag = (t?.tagName || '').toUpperCase();
            const editable = (t as any)?.isContentEditable === true;
            const inLocalScope = !!t?.closest('[data-hotkey-scope="local"]');
            if (inLocalScope || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || editable) {
              event.preventDefault();
            }
          }}>
          <DialogHeader className='sr-only'>
            <DialogTitle>Excalidraw</DialogTitle>
            <DialogDescription>Interactive whiteboard canvas</DialogDescription>
          </DialogHeader>
          <div className='excalidraw-canvas flex-1 min-h-0 w-full overflow-hidden bg-mainbackground'>
            {open && isLoaded && (
              <ExcalidrawWrapper
                key={theme}
                className='w-full h-full [&_.excalidraw]:h-full [&_.excalidraw-container]:h-full'
                theme={theme as 'light' | 'dark'}
                viewBackgroundColor={viewBackgroundColor}
                initialData={{
                  elements: [],
                  appState: {
                    theme: theme as 'light' | 'dark',
                    viewBackgroundColor,
                  },
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ExcalidrawDialogTrigger;
